import React, { createContext, useState, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import User from "../models/User";
import CognitoSession from "../models/CognitoSession";
import { refreshTokens } from "../services/user-service";
import SubscriptionPlan from "../models/SubscriptionPlan";
import {getSubscriptionPlan} from "../services/subscription-service";

type AuthContextType = {
    user: User | null;
    subscriptionPlan: SubscriptionPlan | null;
    isAuthenticated: boolean;
    createSession: (session: CognitoSession) => void;
    clearSession: () => void;
    refreshSession: () => Promise<void>;
};

let clearSessionCallback: (() => void) | null = null;

export const setGlobalClearSessionCallback = (cb: () => void) => {
    clearSessionCallback = cb;
};

export const getGlobalClearSessionCallback = () => clearSessionCallback;

export const getInitialAuthState = () => {
    return !!(localStorage.getItem('accessToken') && localStorage.getItem('refreshToken'));
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [subscriptionPlan, setSubscriptionPlan] = useState<SubscriptionPlan | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(getInitialAuthState());

    const parseIdToken = (idToken: string): User => {
        try {
            return jwtDecode(idToken);
        } catch (err: any) {
            console.error(err);
            return {};
        }
    };

    const createSession = async ({ idToken, accessToken, refreshToken, user}: CognitoSession) => {
        localStorage.setItem('idToken', idToken ? idToken : '');
        localStorage.setItem('accessToken', accessToken ? accessToken : '');
        localStorage.setItem('refreshToken', refreshToken ? refreshToken : '');

        setUser(user ?? null);
        setIsAuthenticated(true);
        localStorage.setItem('userUuid', user?.uuid ?? '');

        if (user?.subscriptionPlanId) {
            try {
                const response = await getSubscriptionPlan(user.subscriptionPlanId);
                if (response.status === 200 && response.data) {
                    setSubscriptionPlan(response.data);
                }
            } catch (err: any) {
                console.error('Unable to load subscription plan', err);
            }
        }
    };

    const clearSession = () => {
        localStorage.clear();
        setUser(null);
        setSubscriptionPlan(null);
        setIsAuthenticated(false);
    };

    const refreshSession = async () => {
        const refreshToken = localStorage.getItem('refreshToken');
        const userUuid = localStorage.getItem('userUuid');

        if (refreshToken && userUuid) {
            try {
                const response = await refreshTokens(refreshToken, { uuid: userUuid })
                if (response.status === 200 && response.data) {
                    const {idToken, accessToken} = response.data;
                    if (idToken && accessToken) {
                        localStorage.setItem('idToken', idToken);
                        localStorage.setItem('accessToken', accessToken);

                        const user = response.data.user;

                        setUser(user ?? null);
                        setIsAuthenticated(true);
                        localStorage.setItem('userUuid', user?.uuid ?? '');

                        if (user?.subscriptionPlanId) {
                            try {
                                const response = await getSubscriptionPlan(user.subscriptionPlanId);
                                if (response.status === 200 && response.data) {
                                    setSubscriptionPlan(response.data);
                                }
                            } catch (err: any) {
                                console.error('Unable to load subscription plan', err);
                            }
                        }
                    } else {
                        console.error('No valid access or id token');
                        clearSession();
                    }
                } else {
                    console.error('Token refresh failed');
                    clearSession();
                }
            } catch (err: any) {
                console.error('Token refresh failed:', err);
                clearSession();
            }
        } else {
            clearSession();
        }
    };

    useEffect(() => {
        refreshSession()
    }, []);

    useEffect(() => {
        if (!isAuthenticated) return;
        const refreshInterval = setInterval(async () => {
            refreshSession();
        }, 55 * 60 * 1000); // 55 minutes
        return () => clearInterval(refreshInterval);
    }, [isAuthenticated]);

    useEffect(() => {
        setGlobalClearSessionCallback(() => {
            clearSession();
        });
    }, []);

    return (
        <AuthContext.Provider value={{ user, subscriptionPlan, isAuthenticated, createSession: createSession, clearSession: clearSession, refreshSession: refreshSession }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};